import { getAppConfigSingleton } from "@expert/app-config";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useGaiaWebsocket, type GaiaWebsocketContext } from "@expert/gaia";
import { useVerifiedCustomer, useVerifiedCustomerStore } from "@expert/sales-tools";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { useAnalytics } from "@/analytics";
import { env } from "@/config";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { isChatTask, sdkEventBus, useActiveTask, usePartner, useSessionId, type VoiceTask } from "@/sdk";
import { useSessionSummaryStore } from "@/session-summary";
import { getHashedValue, removeCountryCode } from "@/shared-utils";

export interface ExtraSessionSummaryPayloadProps {
    billAccountType: string;
    contactMethod: string;
    remarkLevel: string;
    remarkType: string;
}

export interface GaiaSessionContext {
    realtime_tools?: string;
    hashed_mdn?: string;
    mdn?: string;
    country_code?: string;
    client_channel_id?: string;
    locale?: string;
    account_no?: string;
    app_commit_hash?: string;
    app_name?: string;
    app_path?: string;
    app_platform?: string;
    bill_account_type?: string;
    contact_method?: string;
    device_browser_type?: string;
    device_browser_version?: string;
    device_cookies_enabled?: string;
    device_is_browser?: string;
    device_language?: string;
    device_model?: string;
    device_os?: string;
    device_os_version?: string;
    device_type?: string;
    device_user_agent?: string;
    device_user_browser_id?: string;
    device_vendor?: string;
    dispatch_timestamp?: string;
    platform?: string;
    remark_level?: string;
    remark_text?: string;
    remark_type?: string;
    resolution_status?: string;
    scopes?: string;
    task_type?: string;
    type?: string;
    extra_data?: string;
    call_sid?: string;
    contact_id?: string;
    emp_id?: string;
    expert_id?: string;
    session_id?: string;
    site?: string;
    support_session_id?: string;
    task_id?: string;
    user_browser_id?: string;
    username?: string;
    worker_id?: string;
    identities?: string;
}

const extraPropsToContextMap: Record<keyof ExtraSessionSummaryPayloadProps, keyof GaiaSessionContext> = {
    billAccountType: "bill_account_type",
    contactMethod: "contact_method",
    remarkLevel: "remark_level",
    remarkType: "remark_type",
};

function stringifyContextValue(value: unknown): string | undefined {
    if (value == null || value === "") return undefined;
    // oxlint-disable-next-line typescript/no-base-to-string
    return String(value);
}

function mapExtraPropsToContext(
    extraProps: Partial<ExtraSessionSummaryPayloadProps> | undefined,
): Partial<GaiaSessionContext> {
    if (!extraProps) return {};
    const mapped: Partial<GaiaSessionContext> = {};
    for (const [key, value] of Object.entries(extraProps)) {
        const contextKey = extraPropsToContextMap[key as keyof ExtraSessionSummaryPayloadProps];
        if (contextKey && value) {
            mapped[contextKey] = value;
        }
    }
    return mapped;
}

export const useSetGaiaSessionContext = () => {
    const gaiaWS = useGaiaWebsocket();
    const partner = usePartner();
    const sessionId = useSessionId();
    const { dispatcher } = useAnalytics();
    const appConfig = getAppConfigSingleton();
    const extraSessionSummaryPayloadProps = appConfig.getPartnersConfig(partner).extraSessionSummaryPayloadProps;

    const { features } = useFeatures<WorkspaceFeatures>();
    const realtimeAttributes = features ? getRealtimeAttributes(features) : undefined;
    const task = useActiveTask();
    const callSid = (task as VoiceTask)?.agentCallId;

    useEffect(() => {
        const sessionStartedSubscription = sdkEventBus.on("call_accepted", async (task) => {
            if (!realtimeAttributes || !gaiaWS || gaiaWS.loading) return;

            const ctx = await dispatcher.resolveContext();
            const { identities, extra, app, device, scopes } = ctx;
            const { accountNumber, mdn: verifiedMdn } =
                useVerifiedCustomerStore.getState().getVerifiedCustomer(sessionId).customer ?? {};
            const { resolutionStatus, summary, category, subcategory } = useSessionSummaryStore.getState();

            const resolvedMdn = verifiedMdn ?? identities.mdn;
            const formattedMdn = resolvedMdn ? removeCountryCode(resolvedMdn) : undefined;
            const [hashedMdn, hashedExtra, hashedIdentities] = await Promise.all([
                identities.HashedMdn ?? (formattedMdn ? getHashedValue(formattedMdn) : Promise.resolve(undefined)),
                getHashedValue(JSON.stringify(extra)),
                getHashedValue(JSON.stringify(identities)),
            ]);

            const fieldValues: Record<string, string | undefined> = {
                realtime_tools: realtimeAttributes,
                account_no: accountNumber ?? identities.AccountNumber,
                hashed_mdn: hashedMdn,
                mdn: resolvedMdn,
                device_user_browser_id: identities.userBrowserId,
                remark_text: summary,
                resolution_status: resolutionStatus ?? undefined,
                task_type: typeof extra.taskType === "string" ? extra.taskType : "voice",
                type: "businessEvent",
                extra_data: hashedExtra,
                call_sid: identities.CallSid ?? task.agentCallId,
                contact_id: identities.ContactId,
                emp_id: identities.EmpId,
                expert_id: identities.ExpertId,
                session_id: identities.SessionId,
                site: identities.Site,
                support_session_id: identities.SupportSessionId,
                task_id: identities.TaskId,
                user_browser_id: identities.userBrowserId,
                username: identities.Username,
                worker_id: identities.WorkerId,
                identities: hashedIdentities,
                category,
                subcategory,
                app_commit_hash: stringifyContextValue(app.commitHash),
                app_name: stringifyContextValue(app.appName),
                app_path: stringifyContextValue(app.appPath),
                app_platform: stringifyContextValue(app.platform),
                platform: stringifyContextValue(app.platform),
                device_browser_type: stringifyContextValue(device.browserType),
                device_browser_version: stringifyContextValue(device.browserVersion),
                device_cookies_enabled: stringifyContextValue(device.cookiesEnabled),
                device_is_browser: stringifyContextValue(device.isBrowser),
                device_language: stringifyContextValue(device.deviceLanguage),
                device_model: stringifyContextValue(device.model),
                device_os: stringifyContextValue(device.os),
                device_os_version: stringifyContextValue(device.osVersion),
                device_type: stringifyContextValue(device.deviceType),
                device_user_agent: stringifyContextValue(device.userAgent),
                device_vendor: stringifyContextValue(device.vendor),
                scopes: scopes.length > 0 ? scopes.join(",") : undefined,
                ...mapExtraPropsToContext(extraSessionSummaryPayloadProps),
            };

            const properties = Object.entries(fieldValues)
                .filter((entry): entry is [string, string] => entry[1] != null && entry[1] !== "")
                .map(([field, value]) => ({ field, value, action: "add" as const }));

            gaiaWS.sendJsonMessage({
                action: "update-session-context",
                correlationId: sessionId,
                sessionId: task.agentCallId,
                sessionGroupId: sessionId,
                partner,
                context: { properties },
            });
        });

        return () => sessionStartedSubscription();
    }, [dispatcher, extraSessionSummaryPayloadProps, gaiaWS, partner, realtimeAttributes, sessionId]);

    useEffect(() => {
        const unsubscribe = updateChatSessionContextOnSessionStarted({
            realtimeAttributes,
            gaiaWS,
            resolveContext: () => dispatcher.resolveContext(),
            getVerifiedCustomer: (sessionId) =>
                useVerifiedCustomerStore.getState().getVerifiedCustomer(sessionId).customer,
        });
        return unsubscribe;
    }, [gaiaWS, realtimeAttributes, dispatcher]);

    useEffect(() => {
        if (task?.locale && gaiaWS && !gaiaWS.loading) {
            gaiaWS.sendJsonMessage({
                action: "update-session-context",
                correlationId: sessionId,
                sessionId: callSid ?? sessionId,
                sessionGroupId: sessionId,
                partner,
                context: {
                    properties: [
                        {
                            field: "locale",
                            value: task.locale,
                            action: "add",
                        },
                    ],
                },
            });
        }
    }, [callSid, gaiaWS, partner, sessionId, task?.locale]);

    const { accountNumber, mdn: verifiedMdn } = useVerifiedCustomer(sessionId) ?? {};
    // Deliberately unfiltered. The partner blocklists exist to stop unusable values reaching the
    // partner notes API; GAIA is a different consumer, and changing which MDN it sees would alter
    // summarisation and session context for every partner. That belongs in its own change with its
    // own review, not this fix. See `session-summary/summaryDispatchGate.ts`.
    const taskMdn = useTaskMdn({
        blocklistedNumbers: undefined,
        blocklistedPrefixes: undefined,
    });
    const mdn = verifiedMdn || taskMdn || "";
    const { resolutionStatus, summary, category, subcategory } = useSessionSummaryStore(
        useShallow((state) => ({
            resolutionStatus: state.resolutionStatus,
            summary: state.summary,
            category: state.category,
            subcategory: state.subcategory,
        })),
    );

    useEffect(() => {
        if (!gaiaWS || gaiaWS.loading || !callSid) return;

        const formattedMdn = mdn ? removeCountryCode(mdn) : undefined;

        void (async () => {
            const properties: { field: string; value: string; action: "add" }[] = [];

            if (accountNumber) properties.push({ field: "account_no", value: accountNumber, action: "add" });
            if (formattedMdn) {
                properties.push({ field: "mdn", value: formattedMdn, action: "add" });
                properties.push({ field: "hashed_mdn", value: await getHashedValue(formattedMdn), action: "add" });
            }
            if (resolutionStatus)
                properties.push({ field: "resolution_status", value: resolutionStatus, action: "add" });
            if (summary) properties.push({ field: "remark_text", value: summary, action: "add" });
            if (category) properties.push({ field: "category", value: category, action: "add" });
            if (subcategory) properties.push({ field: "subcategory", value: subcategory, action: "add" });

            if (extraSessionSummaryPayloadProps) {
                const mapped = mapExtraPropsToContext(extraSessionSummaryPayloadProps);
                for (const [field, value] of Object.entries(mapped)) {
                    if (value) {
                        properties.push({ field, value, action: "add" });
                    }
                }
            }

            if (properties.length === 0) return;

            gaiaWS.sendJsonMessage({
                action: "update-session-context",
                correlationId: sessionId,
                sessionId: callSid,
                sessionGroupId: sessionId,
                partner,
                context: { properties },
            });
        })();
    }, [
        accountNumber,
        callSid,
        category,
        extraSessionSummaryPayloadProps,
        gaiaWS,
        mdn,
        partner,
        resolutionStatus,
        sessionId,
        subcategory,
        summary,
    ]);
};

export interface UpdateChatSessionContextProps {
    realtimeAttributes: string | undefined;
    gaiaWS: GaiaWebsocketContext | null;
    resolveContext: () => Promise<{ identities: Record<string, string | undefined> }>;
    getVerifiedCustomer: (sessionId: string) => { accountNumber?: string; mdn?: string } | null | undefined;
}

export function updateChatSessionContextOnSessionStarted({
    realtimeAttributes,
    gaiaWS,
    resolveContext,
    getVerifiedCustomer,
}: UpdateChatSessionContextProps): () => void {
    const unsubscribe = sdkEventBus.on("session_changed", ({ action, session }) => {
        if (!realtimeAttributes || !gaiaWS || gaiaWS.loading || action !== "start" || !isChatTask(session.currentTask))
            return;

        const chatTask = session.currentTask;

        void resolveContext().then((ctx) => {
            const { accountNumber, mdn: verifiedMdn } = getVerifiedCustomer(session.id) ?? {};

            const message = buildChatSessionContextMessage({
                realtimeAttributes,
                identities: ctx.identities,
                accountNumber,
                verifiedMdn,
                chatTaskType: chatTask.type,
                chatTaskId: chatTask.id,
                chatId: chatTask.chatId,
                chatPartner: chatTask.partner,
                sessionId: session.id,
            });

            gaiaWS.sendJsonMessage(message);
        });
    });

    return unsubscribe;
}

export interface ChatSessionContextInput {
    realtimeAttributes: string;
    identities: Record<string, string | undefined>;
    accountNumber: string | undefined;
    verifiedMdn: string | undefined;
    chatTaskType: string;
    chatTaskId: string;
    chatId: string;
    chatPartner: string;
    sessionId: string;
}

export function buildChatSessionContextMessage({
    realtimeAttributes,
    identities,
    accountNumber,
    verifiedMdn,
    chatTaskType,
    chatTaskId,
    chatId,
    chatPartner,
    sessionId,
}: ChatSessionContextInput) {
    const fieldValues: Record<string, string | undefined> = {
        realtime_tools: realtimeAttributes,
        account_no: accountNumber,
        mdn: verifiedMdn,
        device_user_browser_id: identities.userBrowserId,
        task_type: chatTaskType,
        emp_id: identities.EmpId,
        expert_id: identities.ExpertId,
        session_id: sessionId,
        site: identities.Site,
        support_session_id: sessionId,
        task_id: chatTaskId,
        user_browser_id: identities.userBrowserId,
        username: identities.Username,
        worker_id: identities.WorkerId,
    };

    const properties = Object.entries(fieldValues)
        .filter((entry): entry is [string, string] => entry[1] != null && entry[1] !== "")
        .map(([field, value]) => ({ field, value, action: "add" as const }));

    return {
        action: "update-session-context" as const,
        correlationId: sessionId,
        sessionId: chatId,
        sessionGroupId: sessionId,
        partner: chatPartner,
        context: { properties },
    };
}

export const getRealtimeAttributes = (workspaceFeatures: WorkspaceFeatures) => {
    const realtimeTools = [];

    if (env.channel === "chat") {
        realtimeTools.push("gaia-device-cues");
        return realtimeTools.join(",");
    }

    const {
        isRapportEnabled,
        isGaiaMultiStepSummaryEnabled,
        isGaiaCallResolutionEnabled,
        isGuidedFlowRecommendationEnabled,
    } = workspaceFeatures;

    // TODO: replace "gaia-post-call-summary" with "gaia-multistep-call-summarization" when post-call-summary is deprecated on gaia side
    realtimeTools.push("gaia-post-call-summary", "gaia-device-cues", "gaia-location-cues");

    if (isGaiaCallResolutionEnabled) {
        realtimeTools.push("gaia-call-resolution");
    }
    if (isGaiaMultiStepSummaryEnabled) {
        realtimeTools.push("gaia-multistep-call-summarization");
    }
    if (isGuidedFlowRecommendationEnabled) {
        realtimeTools.push("gaia-call-category-cues");
    }
    if (isRapportEnabled) {
        realtimeTools.push("gaia-rapport-checklist");
    }

    return realtimeTools.join(",");
};
